import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// LAYOUT GLOBAL
import BaseLayout from "../components/layout/BaseLayout";

// ✅ Firebase
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

// LOGIN & DASHBOARD
import Login from "../pages/Login";
import Dashboard from "../pages/Dashboard";

// USUÁRIOS
import Users from "../pages/Users";
import UsuariosCreate from "../pages/UsuariosCreate";

// OPERADORES / ATENDENTES
import OperatorsList from "../pages/OperatorsList";
import AtendentesCreate from "../pages/AtendentesCreate";

// MATCHS
import Matchs from "../pages/Matchs";

// PRODUTOS
import ProdutosBeleza from "../pages/ProdutosBeleza";

// IA
import IAdoAmor from "../pages/IAdoAmor";
import IADashboard from "../pages/IADashboard";

// NOTIFICAÇÕES
import EnviarNotificacao from "../pages/EnviarNotificacao";
import UpdatesFeed from "../pages/UpdatesFeed";
import HistoricoNotificacoes from "../pages/HistoricoNotificacoes";
import HistoricoUpdates from "../pages/HistoricoUpdates";

// PLANOS
import Planos from "../pages/Planos";
import PlanoUsuario from "../pages/PlanoUsuario";
import ChavesAtivacao from "../pages/ChavesAtivacao";

// ✅ ASSINATURAS (NOVO)
import Assinaturas from "../pages/Assinaturas";

// VERIFICAÇÃO DE PERFIL
import Verificacoes from "../pages/Verificacoes";
import VerificacoesPendentes from "../pages/VerificacoesPendentes";
import VerificacoesAprovadas from "../pages/VerificacoesAprovadas";
import VerificacoesRejeitadas from "../pages/VerificacoesRejeitadas";
import VerificacaoDetalhe from "../pages/VerificacaoDetalhe";

// ✅ HORÓSCOPO SEMANAL (NOVO)
import HoroscopoSemanal from "../pages/HoroscopoSemanal";

// ✅ MAPA ASTRAL LEADS (NOVO)
import AstroMapLeads from "../pages/AstroMapLeads";

// ✅ EBOOKS (NOVO)
import Ebooks from "../pages/Ebooks";

// ============================
// 🔒 HELPERS
// ============================
async function checkIsAdmin(uid) {
  try {
    const ref = doc(db, "admins", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) return false;

    const data = snap.data() || {};
    if (data.active === false) return false;

    return true;
  } catch (_) {
    return false;
  }
}

// ============================
// 🔒 ROTA PROTEGIDA (AUTH + ADMIN)
// ============================
function ProtectedRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      // reset básico
      setLoading(true);

      if (!user) {
        setAllowed(false);
        setLoading(false);
        return;
      }

      const ok = await checkIsAdmin(user.uid);

      if (!ok) {
        // 🔒 Se não for admin, derruba sessão e bloqueia
        setAllowed(false);
        setLoading(false);

        // signOut assíncrono (sem travar render)
        try {
          await signOut(auth);
        } catch (_) {}

        return;
      }

      // ✅ Admin ok
      setAllowed(true);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#fff5f9",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              border: "4px solid #ffc7dd",
              borderTopColor: "#ff4f9a",
              margin: "0 auto 12px",
              animation: "spin 0.9s linear infinite",
            }}
          />
          <div style={{ fontWeight: 800, color: "#d45a97" }}>Carregando...</div>

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      </div>
    );
  }

  if (!allowed) return <Navigate to="/" replace />;

  return children;
}

// ============================
// 📍 ROTAS DO SISTEMA
// ============================
export default function AppRoutes() {
  return (
    <Routes>
      {/* LOGIN */}
      <Route path="/" element={<Login />} />

      {/* DASHBOARD */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Dashboard />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          USUÁRIOS
      ============================ */}
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Users />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/usuarios/novo"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <UsuariosCreate />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/usuarios-gerenciar"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Users />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          OPERADORES
      ============================ */}
      <Route
        path="/operadores"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <OperatorsList />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/operadores/novo"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <AtendentesCreate />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/operadores/editar/:id"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <AtendentesCreate isEditMode={true} />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          MATCHS
      ============================ */}
      <Route
        path="/matchs"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Matchs />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          PRODUTOS
      ============================ */}
      <Route
        path="/produtos-beleza"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <ProdutosBeleza />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          IA
      ============================ */}
      <Route
        path="/ia"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <IAdoAmor />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ia-dashboard"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <IADashboard />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          NOTIFICAÇÕES
      ============================ */}
      <Route
        path="/updates-feed"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <UpdatesFeed />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/enviar-notificacao"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <EnviarNotificacao />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/historico-notificacoes"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <HistoricoNotificacoes />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/historico-updates"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <HistoricoUpdates />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          PLANOS
      ============================ */}
      <Route
        path="/planos"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Planos />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/usuario-plano"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <PlanoUsuario />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chaves"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <ChavesAtivacao />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          ✅ ASSINATURAS
      ============================ */}
      <Route
        path="/assinaturas"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Assinaturas />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          ✅ HORÓSCOPO SEMANAL
      ============================ */}
      <Route
        path="/horoscopo-semanal"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <HoroscopoSemanal />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          ✅ EBOOKS
      ============================ */}
      <Route
        path="/ebooks"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Ebooks />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          ✅ MAPA ASTRAL — LEADS
          ✅ CORRIGIDO: agora também fica dentro do BaseLayout
      ============================ */}
      <Route
        path="/astro-map-leads"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <AstroMapLeads />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* ============================
          VERIFICAÇÕES
      ============================ */}
      <Route
        path="/verificacoes"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <Verificacoes />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/verificacoes/pendentes"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <VerificacoesPendentes />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/verificacoes/aprovadas"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <VerificacoesAprovadas />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/verificacoes/rejeitadas"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <VerificacoesRejeitadas />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/verificacao-detalhe"
        element={
          <ProtectedRoute>
            <BaseLayout>
              <VerificacaoDetalhe />
            </BaseLayout>
          </ProtectedRoute>
        }
      />

      {/* CATCH ALL */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
